import styled from 'styled-components';

/**
 * Styled component for ETTX heading 1 text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Heading 1 Text with color
 */
export const Heading1Text = styled.span`
  font-weight: 700;
  font-size: 32px;
  line-height: 44px;
  letter-spacing: 0.4px;
  color: ${({ color }) => color || '#000000'};
`;

export const Spacer = styled.div`
  width: 100%;
  height: ${({ $height }) => $height || '20px'};
`;

/**
 * Styled component for ETTX heading 2 text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Heading 2 Text with color
 */
export const Heading2Text = styled.span`
  font-weight: 600;
  font-size: 28px;
  line-height: 40px;
  letter-spacing: 0.2px;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX heading 3 text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Heading 3 Text with color
 */
export const Heading3Text = styled.span`
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.2px;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX subheader text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Subheader Text with color
 */
export const SubheaderText = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.2px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX subtitle 2 text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Subtitle 2 Text with color
 */
export const Subtitle2Text = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.2px;
  text-align: left;
  color: ${({ theme, primary }) =>
    primary ? theme.COLOR_PRIMARY : theme.COLOR_BLACK};
`;

/**
 * Styled component for ETTX subtitle 2 Italic text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Subtitle 2 Text with color
 */
export const Subtitle3Text = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.2px;
  text-align: left;
  font-style: ${({ primary }) => (primary ? 'normal' : 'italic')};
  color: ${({ theme, primary }) =>
    primary ? theme.COLOR_GREY_TEXT : theme.COLOR_GREY_TEXT2};
`;

/**
 * Styled component for ETTX body text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Body Text with color
 */
export const BodyText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.2px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
  width: ${({ $width }) => $width || 'unset'};
  display: flex;
  align-items: center;
`;

/**
 * Styled component for ETTX button text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Button Text with color
 */
export const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.8px;
  text-align: left;
  text-align: center;
  text-align: center;
  font-feature-settings:
    'clig' off,
    'liga' off;
  color: ${({ color }) => color || '#ffffff'};
`;

/**
 * Styled component for ETTX caption text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Caption Text with color
 */
export const CaptionText = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.4px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX table headers text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Table Headers Text with color
 */
export const TableHeadersText = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
  letter-spacing: 0.4px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX large body text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The Large Body Text with color
 */
export const BodyTextLarge = styled.span`
  font-size: 18px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.4px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
`;

/**
 * Styled component for ETTX view small title text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The View Small Title Text with color
 */
export const ViewSmallTitleText = styled.span`
  font-size: 11px;
  font-weight: 700;
  line-height: 13px;
  letter-spacing: 0.4px;
  text-align: left;
  color: ${({ color }) => color || '#000000'};
  text-transform: ${({ $toUpper }) => ($toUpper ? 'uppercase' : 'unset')};
  margin-bottom: 9px;
`;

/**
 * Styled component for ETTX view small title text
 * @param {string} color - The color of the text
 * @returns {JSX.Element} The View Small Title Text with color
 */
export const SmallTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  line-height: 13px;
  letter-spacing: 0.4px;
  text-align: left;
  color: ${({ color, theme }) => color ?? theme.COLOR_GREY_TEXT};
  text-transform: ${({ $toUpper }) => ($toUpper ? 'uppercase' : 'unset')};
  margin-bottom: 9px;
`;

/**
 * Default export of all styled components
 */
export default {
  Heading1Text,
  Heading2Text,
  Heading3Text,
  SubheaderText,
  Subtitle2Text,
  BodyText,
  ButtonText,
  CaptionText,
  TableHeadersText,
  BodyTextLarge,
  ViewSmallTitleText,
  SmallTitle,
};
